import { STAGING_COLOR } from '@/pwa/web-manifest';
import { currentEnvironment } from '@/shell/environment';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'dgb-console:theme-mode';

/**
 * Persisted `localStorage` marker (Task 12.2, review-gate B3): set immediately before a
 * `PATCH /v1/profile` write-through for `themePreference` is issued, cleared only on confirmed
 * success. While set, `ThemeProvider.hydrateFromBackend` refuses to adopt a differing backend
 * value (a pending local change always wins over a stale backend read) and a retry-on-next-load
 * effect re-issues the write. Survives a page reload, unlike an in-memory-only flag would.
 *
 * code-review (2026-07-15, F4): the marker stores the pending WRITE'S TARGET `ThemeMode` (not a
 * bare boolean) so overlapping writes are last-write-wins-safe. Two rapid `setMode` calls each
 * mark this key with their own target and each issue their own PATCH; if the OLDER write's PATCH
 * resolves after the NEWER one was issued, its `clearThemeSyncPending` call only removes the
 * marker when the value it confirmed still matches what is currently stored — since the newer
 * call already overwrote it with its own target, the older write's completion is a safe no-op and
 * the marker keeps blocking `hydrateFromBackend` until the newer (truly latest) write itself
 * confirms and clears a matching value. Without this, the older write's premature clear could let
 * a stale backend GET silently revert a newer, still-in-flight local choice.
 */
export const THEME_PENDING_SYNC_KEY = 'dgb-console:theme-pending-sync';

/**
 * The `--frame` light/dark values from the token layer (`styles/globals.css`, Brand Kit 1.0.0),
 * duplicated here as literal hex strings because this module (and the `index.html` bootstrap
 * script it mirrors, Task 2.4) must set the `theme-color` meta tag before any CSS has loaded. The
 * frame is what surrounds the content sheet, so it is the colour the browser's own chrome meets.
 */
export const THEME_COLORS: Record<ResolvedTheme, string> = {
  light: '#ecf0f3',
  dark: '#07121a',
};

const THEME_MODES: readonly ThemeMode[] = ['light', 'dark', 'system'];

export function isThemeMode(value: unknown): value is ThemeMode {
  return typeof value === 'string' && (THEME_MODES as readonly string[]).includes(value);
}

export function getSystemTheme(): ResolvedTheme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function resolveTheme(mode: ThemeMode): ResolvedTheme {
  return mode === 'system' ? getSystemTheme() : mode;
}

/** Parses the persisted mode defensively — any malformed or absent value falls back to `'system'`,
 * mirroring `active-context-store.ts`'s own read-defensively-fall-back-to-a-safe-default pattern. */
export function readStoredMode(): ThemeMode {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    return isThemeMode(raw) ? raw : 'system';
  } catch {
    return 'system';
  }
}

export function writeStoredMode(mode: ThemeMode): void {
  localStorage.setItem(THEME_STORAGE_KEY, mode);
}

/** Mirrors `readStoredMode`'s defensive-read pattern: any error reading `localStorage` is treated
 * as "no pending sync," never as a false positive that would block a legitimate hydration. */
export function hasPendingThemeSync(): boolean {
  try {
    return isThemeMode(localStorage.getItem(THEME_PENDING_SYNC_KEY));
  } catch {
    return false;
  }
}

/** Marks `target` as the latest write in flight — overwrites any prior pending target, which is
 * exactly the "supersede the older write" behavior an overlapping second call needs (F4). */
export function markThemeSyncPending(target: ThemeMode): void {
  localStorage.setItem(THEME_PENDING_SYNC_KEY, target);
}

/** Clears the marker only when `confirmed` still matches the currently stored pending target
 * (F4) — a stale write's own confirmation must never clear a marker a newer write has already
 * overwritten with its own target. Defensive-read mirrors `hasPendingThemeSync`: a storage-access
 * failure here leaves the marker as-is (the retry-on-next-load path is the backstop), rather than
 * risking an unhandled rejection in the async PATCH handler that calls this. */
export function clearThemeSyncPending(confirmed: ThemeMode): void {
  try {
    if (localStorage.getItem(THEME_PENDING_SYNC_KEY) === confirmed) {
      localStorage.removeItem(THEME_PENDING_SYNC_KEY);
    }
  } catch {
    // No-op — see doc comment above.
  }
}

/**
 * Applies a resolved theme to the document (Resolution 15): toggles the `.dark` class consumed by
 * `globals.css`'s `@custom-variant dark (&:is(.dark *));`, sets `color-scheme` so native form
 * controls/scrollbars follow the same resolution, and updates the `theme-color` meta tag so the
 * browser chrome (e.g. a mobile address bar) matches. On staging the chrome stays orange in both
 * themes (#303), as `index.html` and the manifest have it.
 */
export function applyResolvedTheme(
  resolved: ResolvedTheme,
  environment: string = currentEnvironment(),
): void {
  document.documentElement.classList.toggle('dark', resolved === 'dark');
  document.documentElement.style.colorScheme = resolved;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute(
      'content',
      environment === 'staging' ? STAGING_COLOR : THEME_COLORS[resolved],
    );
  }
}
